<script lang="ts">
  import { DepthEdits, type BrushSettings } from '../depth/brush';
  import { estimateMs, recordMs } from '../eta';
  import { mlEstimateMs, mlStatus } from '../ml/client.svelte';
  import { computeMode, computeTarget } from '../server/compute.svelte';
  import { serverJob } from '../server/jobs.svelte';
  import { sceneStore } from '../scene/store.svelte';
  import { formatDuration, t } from '../i18n/index.svelte';
  import { depthToRgba, downloadBlob } from '../image';
  import { outputSize, processDepth, renderStereogram, sourceDepth, texturePixels, type Inputs } from '../pipeline';
  import { assets, persist, studio } from '../state.svelte';
  import { maxSeparationPx, type DepthData, type RgbaData } from '../types';
  import { CancelledError, interactivePool } from '../wasm/pool';
  import BrushTools from './BrushTools.svelte';
  import SegmentTools from './SegmentTools.svelte';
  import { segmentImage } from '../ml/client.svelte';
  import type { RegionOp } from '../depth/brush';
  import type { SegmentPoint } from '../ml/protocol';
  import PublishDialog from './PublishDialog.svelte';
  import { caps } from '../config.svelte';
  import DepthPanel from './DepthPanel.svelte';
  import OutputPanel from './OutputPanel.svelte';
  import Preview from './Preview.svelte';
  import SourcePanel from './SourcePanel.svelte';
  import StereoPanel from './StereoPanel.svelte';
  import TexturePanel from './TexturePanel.svelte';

  let stereo = $state.raw<RgbaData | null>(null);
  let depth = $state.raw<DepthData | null>(null);
  let textureTile = $state.raw<RgbaData | null>(null);
  let busy = $state(false);
  let error = $state('');
  let lastMs = $state(0);
  let tab = $state('stereo');

  // Brush retouching: a delta layer over the processed depth.
  let base: DepthData | null = null;
  let edits = $state.raw<DepthEdits | null>(null);
  let editsVersion = $state(0);
  let brushOn = $state(false);
  let brush = $state<BrushSettings>({ mode: 'raise', radius: 40, strength: 0.5 });
  let paintRaf = 0;

  // Click segmentation (SlimSAM) on the source image.
  let segOn = $state(false);
  let segPoints = $state<SegmentPoint[]>([]);
  let segMask = $state.raw<Uint8Array | null>(null);
  let segBusy = $state(false);
  let segError = $state('');
  const canSegment = $derived(studio.sourceKind === 'image' && !!assets.sourceImage);
  const imageKey = $derived(
    assets.sourceImage ? `${assets.sourceName}:${assets.sourceImage.width}x${assets.sourceImage.height}` : '',
  );

  async function addSegPoint(x: number, y: number, label: 0 | 1) {
    const img = assets.sourceImage;
    if (!img || !base) return;
    // Output pixel -> source image pixel (same aspect ratio).
    const sx = (x * img.width) / base.width;
    const sy = (y * img.height) / base.height;
    segPoints = [...segPoints, { x: sx, y: sy, label }];
    segBusy = true;
    segError = '';
    try {
      const { mask } = await segmentImage(img, imageKey, $state.snapshot(segPoints));
      // Nearest-neighbour resample to the output size.
      const out = new Uint8Array(base.width * base.height);
      for (let oy = 0; oy < base.height; oy++) {
        const iy = Math.min(img.height - 1, Math.floor(((oy + 0.5) * img.height) / base.height));
        for (let ox = 0; ox < base.width; ox++) {
          const ix = Math.min(img.width - 1, Math.floor(((ox + 0.5) * img.width) / base.width));
          out[oy * base.width + ox] = mask[iy * img.width + ix];
        }
      }
      segMask = out;
    } catch (e) {
      segError = e instanceof Error ? e.message : String(e);
    } finally {
      segBusy = false;
    }
  }

  function applyRegion(op: RegionOp) {
    if (!base || !segMask) return;
    edits ??= new DepthEdits(base.width, base.height);
    edits.region(base, segMask, op);
    editsVersion++;
  }

  function clearSegment() {
    segPoints = [];
    segMask = null;
  }

  const segPainter = {
    down(x: number, y: number, e: PointerEvent) {
      void addSegPoint(x, y, e.shiftKey || e.button === 2 ? 0 : 1);
    },
    move() {},
    up() {},
  };

  $effect(() => {
    // A new image invalidates the selection.
    void imageKey;
    clearSegment();
  });
  $effect(() => {
    if (segOn) brushOn = false;
  });
  $effect(() => {
    if (brushOn) segOn = false;
  });
  let lastDab: [number, number] | null = null;

  const inputs = $derived<Inputs>({
    sourceImage: assets.sourceImage,
    sourceFile: assets.sourceFile,
    uploadedDepth: assets.uploadedDepth,
    textureImage: assets.textureImage,
  });
  const size = $derived(outputSize(studio, inputs));
  const units = $derived(((size[0] * size[1]) / 1e6) * studio.stereo.oversample);
  const estimate = $derived(estimateMs('stereogram', units));

  // Caches so that e.g. a texture change does not re-render 3D text.
  let srcCache: { key: string; ref: unknown; value: DepthData | null } | null = null;
  let procCache: { key: string; src: DepthData; value: DepthData } | null = null;

  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function schedule(delay = 120) {
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  }

  async function run() {
    const gen = ++generation;
    const pool = interactivePool();
    if (busy && pool.queued > 0) pool.cancelAll();
    busy = true;
    error = '';
    const s = $state.snapshot(studio);
    const inp = inputs;
    const [w, h] = size;
    try {
      const srcKey = JSON.stringify([
        s.sourceKind,
        s.sourceKind === 'text'
          ? s.text
          : s.sourceKind === 'scene'
            ? [sceneStore.revision, sceneStore.time]
            : s.heuristic,
        s.edgeThreshold,
        // ML runs where the compute switch says, with the chosen server model.
        s.heuristic === 'ml' ? [computeTarget(), computeTarget() === 'server' ? s.serverModel : ''] : null,
        w,
        h,
      ]);
      const srcRef = s.sourceKind === 'image' ? inp.sourceImage : inp.uploadedDepth;
      let src: DepthData | null;
      if (srcCache && srcCache.key === srcKey && srcCache.ref === srcRef) {
        src = srcCache.value;
      } else {
        src = await sourceDepth(pool, s, inp, w, h);
        if (gen !== generation) return;
        srcCache = { key: srcKey, ref: srcRef, value: src };
        // A new source invalidates retouching.
        edits = null;
      }
      if (!src) {
        stereo = null;
        depth = null;
        return;
      }
      const procKey = JSON.stringify([s.depth, w, h]);
      let processed: DepthData;
      if (procCache && procCache.key === procKey && procCache.src === src) {
        processed = procCache.value;
      } else {
        processed = await processDepth(pool, s, src, w, h);
        if (gen !== generation) return;
        procCache = { key: procKey, src, value: processed };
      }
      base = processed;
      if (edits && (edits.width !== w || edits.height !== h)) edits = null;
      const final = edits && !edits.isEmpty ? edits.apply(processed) : processed;
      depth = final;
      if (s.texture.kind === 'image' && !inp.textureImage) {
        stereo = null;
        error = t('texture.needImage');
        return;
      }
      const t0 = performance.now();
      const out = await renderStereogram(pool, s, final, inp);
      if (gen !== generation) return;
      lastMs = performance.now() - t0;
      recordMs('stereogram', ((w * h) / 1e6) * s.stereo.oversample, lastMs);
      stereo = out;
      // Texture preview tile (cheap).
      const tw = maxSeparationPx(s.stereo);
      if (s.texture.kind !== 'source') {
        textureTile = await pool.call('renderTexture', {
          texture: s.texture,
          width: tw,
          height: Math.min(h, 512),
          seed: s.stereo.seed,
          textureImage: texturePixels(s.texture, inp),
        });
      } else {
        textureTile = inp.sourceImage;
      }
    } catch (e) {
      if (e instanceof CancelledError) return;
      if (gen === generation) error = e instanceof Error ? e.message : String(e);
    } finally {
      if (gen === generation) busy = false;
    }
  }

  $effect(() => {
    // Track every setting and binary input.
    JSON.stringify(studio);
    void [
      assets.sourceImage,
      assets.uploadedDepth,
      assets.textureImage,
      size[0],
      size[1],
      editsVersion,
      computeMode.mode,
      sceneStore.revision,
      sceneStore.time,
    ];
    persist();
    schedule();
  });

  const painter = {
    down(x: number, y: number) {
      if (!base) return;
      edits ??= new DepthEdits(base.width, base.height);
      edits.begin(base, x, y);
      lastDab = null;
      painter.move(x, y);
    },
    move(x: number, y: number) {
      if (!base || !edits) return;
      const b = $state.snapshot(brush);
      // Space dabs along the path so fast strokes stay continuous.
      const spacing = Math.max(1, b.radius / 4);
      const [px, py] = lastDab ?? [x, y];
      const steps = Math.max(1, Math.floor(Math.hypot(x - px, y - py) / spacing));
      for (let k = 1; k <= steps; k++) edits.dab(base, px + ((x - px) * k) / steps, py + ((y - py) * k) / steps, b);
      lastDab = [x, y];
      if (!paintRaf) {
        paintRaf = requestAnimationFrame(() => {
          paintRaf = 0;
          if (base && edits) depth = edits.apply(base);
        });
      }
    },
    up() {
      editsVersion++;
    },
  };

  function undoEdit() {
    edits?.undo();
    editsVersion++;
  }

  function clearEdits() {
    edits?.clear();
    editsVersion++;
  }

  const mlBusy = $derived(busy && studio.sourceKind === 'image' && studio.heuristic === 'ml');

  let preview: Preview;
  const depthImage = $derived.by(() => {
    if (!depth) return null;
    const img = depthToRgba(depth);
    const m = segOn ? segMask : null;
    if (m && m.length * 4 === img.data.length) {
      // Tint the selection.
      for (let i = 0; i < m.length; i++) {
        if (!m[i]) continue;
        const p = i * 4;
        img.data[p] = (img.data[p] + 232) >> 1;
        img.data[p + 1] = (img.data[p + 1] + 194) >> 1;
        img.data[p + 2] = (img.data[p + 2] + 106) >> 1;
      }
    }
    return img;
  });

  let publishOpen = $state(false);

  async function preparePublish() {
    if (!stereo) throw new Error('nothing to publish');
    const png = await interactivePool().call('encodePng', { image: stereo });
    return { file: new Blob([png as Uint8Array<ArrayBuffer>], { type: 'image/png' }), name: 'stereogram.png' };
  }

  /** Settings safe to share: no source photo, no uploaded depth map. */
  function shareableParams() {
    const s = $state.snapshot(studio);
    return {
      source: s.sourceKind,
      text: s.sourceKind === 'text' ? s.text : undefined,
      depth: s.depth,
      texture: s.texture,
      stereo: s.stereo,
      output: s.output,
    };
  }

  function savePng() {
    const canvas = preview.getCanvas();
    const name = tab === 'depth' ? 'depth-preview' : tab === 'texture' ? 'texture' : 'stereogram';
    canvas.toBlob((b) => b && downloadBlob(b, `${name}-${studio.stereo.seed}.png`), 'image/png');
  }

  async function saveDepth16() {
    if (!depth) return;
    const bytes = await interactivePool().call('encodeDepthPng16', { depth });
    downloadBlob(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' }), 'depth16.png');
  }
</script>

<div class="studio">
  <aside class="controls">
    <SourcePanel />
    <OutputPanel {size} fixedAspect={studio.sourceKind === 'image' || studio.sourceKind === 'depth'} />
    <DepthPanel />
    <TexturePanel allowSource={studio.sourceKind === 'image' && !!assets.sourceImage} />
    <StereoPanel />
  </aside>
  <main>
    <Preview
      bind:this={preview}
      bind:active={tab}
      {busy}
      painter={tab === 'depth' && brushOn ? painter : tab === 'depth' && segOn ? segPainter : undefined}
      brushRadius={brush.radius}
      tabs={[
        { id: 'stereo', label: t('preview.stereogram'), image: stereo },
        { id: 'depth', label: t('preview.depth'), image: depthImage },
        { id: 'texture', label: t('preview.texture'), image: textureTile },
      ]}
    >
      {#snippet status()}
        {#if error || segError}
          <span class="error" data-testid="render-error">{error || segError}</span>
        {:else if busy && serverJob.active}
          <span class="muted" data-testid="server-status"
            ><span class="spinner"></span>{serverJob.status === 'queued'
              ? t('server.queued', { n: serverJob.queuePosition ?? 1 })
              : t('server.running', { p: Math.round(serverJob.progress * 100) })} · {t('anim.left', {
              t: formatDuration(serverJob.etaMs),
            })}</span
          >
        {:else if mlBusy && mlStatus.phase === 'download'}
          <span class="muted" data-testid="ml-status"
            ><span class="spinner"></span>{t('ml.downloading', {
              done: (mlStatus.loaded / 1e6).toFixed(0),
              total: (mlStatus.total / 1e6).toFixed(0),
            })}</span
          >
        {:else if mlBusy && (mlStatus.phase === 'init' || mlStatus.phase === 'infer')}
          <span class="muted" data-testid="ml-status"
            ><span class="spinner"></span>{mlStatus.phase === 'init'
              ? t('ml.init')
              : t('ml.inferring', { backend: mlStatus.backend ?? '…' })} · {t('eta.about', {
              t: formatDuration(mlEstimateMs()),
            })}</span
          >
        {:else if busy}
          <span class="muted"
            ><span class="spinner"></span>{t('preview.rendering')} · {t('eta.about', {
              t: formatDuration(estimate),
            })}</span
          >
        {:else if stereo}
          <span class="muted mono" data-testid="render-done"
            >{size[0]}×{size[1]} · ×{studio.stereo.oversample} · {Math.round(lastMs)} ms</span
          >
        {/if}
      {/snippet}
      {#snippet toolbar()}
        {#if tab === 'depth'}
          <BrushTools
            bind:enabled={brushOn}
            bind:settings={brush}
            canUndo={!!edits?.canUndo}
            hasEdits={!!edits && !edits.isEmpty}
            onundo={undoEdit}
            onclear={clearEdits}
            version={editsVersion}
          />
          {#if canSegment}
            <SegmentTools
              bind:enabled={segOn}
              hasMask={!!segMask}
              busy={segBusy}
              points={segPoints.length}
              onapply={applyRegion}
              onclear={clearSegment}
            />
          {/if}
        {/if}
        <button class="primary" disabled={!stereo} onclick={savePng} data-testid="download-png"
          >{t('preview.downloadPng')}</button
        >
        <button disabled={!depth} onclick={saveDepth16} title={t('preview.depth16Hint')}
          >{t('preview.downloadDepth')}</button
        >
        {#if caps.gallery}
          <button disabled={!stereo} onclick={() => (publishOpen = true)} data-testid="publish"
            >{t('publish.button')}</button
          >
        {/if}
      {/snippet}
    </Preview>
  </main>
</div>

<PublishDialog bind:open={publishOpen} prepare={preparePublish} params={shareableParams()} />

<style>
  .studio {
    display: grid;
    grid-template-columns: 340px 1fr;
    height: 100%;
    min-height: 0;
  }
  .controls {
    background: var(--panel);
    border-right: 1px solid var(--line);
    overflow-y: auto;
  }
  main {
    min-width: 0;
    min-height: 0;
  }
  .spinner {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 6px;
    border: 2px solid var(--muted);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    vertical-align: -1px;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (max-width: 820px) {
    .studio {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      height: auto;
    }
    .controls {
      border-right: 0;
      overflow: visible;
    }
    main {
      height: 70vh;
    }
  }
</style>
